import React, { useState, useEffect, useCallback } from "react";
import { swalError, confirmDelete } from "../common/confirm";
import {
  Plus,
  X,
  Shield,
  Activity,
  AlertTriangle,
  Wrench,
  Trash2,
  Calendar,
  History,
} from "lucide-react";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";
import { usePermission } from "../../contexts/PermissionContext";
import linkService, {
  LinkedItem,
  ControlLinkType,
} from "../../services/linkService";
import controlRegisterService, {
  ControlRegister,
  ControlScoreHistoryRow,
  ControlImplementationStep,
} from "../../services/controlRegisterService";
import riskRegisterService, { RiskRegister } from "../../services/riskRegisterService";
import ScoreControlModal from "./ScoreControlModal";
import RiskConfigModal from "../risk/RiskConfigModal";
import ControlConfigModal from "./ControlConfigModal";

// --- Components ---
// Visually distinct from the Risk Flow Canvas (a Y, not a bow-tie diamond) but
// conceptually consistent: same quadrant-card language, same center-node +
// side-panel split, same "+"-to-manage / click-through-to-detail behaviour.

type ThemeColor = "blue" | "green" | "slate";

interface ListNodeProps {
  title: string;
  icon: any;
  color: ThemeColor;
  items: any[];
  onRemove: (id: number) => void;
  className?: string;
  /** When provided, "+" opens the parent's manage flow. */
  onManage?: () => void;
  /** Hides "+" and trash icons (derived / view-elsewhere data). */
  readOnly?: boolean;
  onItemClick?: (id: number) => void;
}

const ListNode = ({
  title,
  icon: Icon,
  color,
  items,
  onRemove,
  className,
  onManage,
  readOnly,
  onItemClick,
}: ListNodeProps) => {
  const theme = {
    blue: {
      border: "border-blue-200",
      bg: "bg-blue-50",
      headerBg: "bg-blue-100",
      text: "text-blue-900",
      icon: "text-blue-600",
    },
    green: {
      border: "border-emerald-200",
      bg: "bg-emerald-50",
      headerBg: "bg-emerald-100",
      text: "text-emerald-900",
      icon: "text-emerald-600",
    },
    slate: {
      border: "border-slate-200",
      bg: "bg-slate-50",
      headerBg: "bg-slate-100",
      text: "text-slate-700",
      icon: "text-slate-500",
    },
  };
  const currentTheme = theme[color];

  return (
    <div
      className={`flex flex-col w-64 border rounded-lg shadow-sm bg-white z-10 transition-all duration-300 hover:shadow-md ${currentTheme.border} ${className}`}
    >
      <div
        className={`px-3 py-2 flex items-center justify-between border-b ${currentTheme.border} ${currentTheme.headerBg}`}
      >
        <div
          className={`flex items-center gap-2 font-bold ${currentTheme.text}`}
        >
          <Icon size={16} className={currentTheme.icon} />
          <span className="uppercase tracking-wide text-[10px]">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="bg-white/60 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-slate-600">
            {items.length}
          </span>
          {!readOnly && onManage && (
            <button
              onClick={onManage}
              className="p-0.5 rounded hover:bg-white/50 transition-colors"
              title={`Manage ${title.toLowerCase()}`}
              data-testid={`BTN_control_canvas_list_${title.toLowerCase()}_add`}
            >
              <Plus size={14} className={currentTheme.text} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 p-2 min-h-[140px] max-h-[200px] overflow-y-auto custom-scrollbar">
        {items.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 text-[10px] italic p-4 text-center">
            {readOnly
              ? `No ${title.toLowerCase()} yet.`
              : `Click + to add ${title.toLowerCase()}.`}
          </div>
        ) : (
          <ul className="space-y-1">
            {items.map((item: any) => (
              <li
                key={item.id}
                className="group flex items-start gap-2 text-xs text-slate-600 bg-slate-50 hover:bg-slate-100 p-1.5 rounded border border-transparent hover:border-slate-200 transition-all"
              >
                <div className="mt-1 w-1 h-1 rounded-full shrink-0 bg-slate-400" />
                {onItemClick ? (
                  <button
                    type="button"
                    onClick={() => onItemClick(item.id)}
                    className={`flex-1 leading-relaxed text-left cursor-pointer hover:underline ${currentTheme.text}`}
                    title="Open details"
                  >
                    {item.text}
                  </button>
                ) : (
                  <span className="flex-1 leading-relaxed">{item.text}</span>
                )}
                {!readOnly && (
                  <button
                    onClick={() => onRemove(item.id)}
                    className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                    title="Remove"
                    data-testid={`BTN_control_canvas_list_${title.toLowerCase()}_item_${item.id}_remove`}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// The Central Control Node (Circular) — the rim is a live SVG progress ring
// driven by the latest score event (fetched from scoreHistory), matching how
// the risk canvas's center node reflects the latest score, not a possibly
// stale rollup field on the control object passed in from elsewhere.
const RING_RADIUS = 80;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ControlNode = ({
  control,
  netScore,
  hasScore,
  signal,
}: {
  control: ControlRegister;
  netScore: number;
  hasScore: boolean;
  signal?: string | null;
}) => {
  const badgeClass = (s?: string | null) => {
    if (s === "BREACH") return "status-badge xs danger";
    if (s === "WARNING") return "status-badge xs warning";
    if (s === "HEALTHY") return "status-badge xs success";
    return "status-badge xs";
  };

  const ringColor = (s?: string | null) => {
    if (s === "BREACH") return "#ef4444";
    if (s === "WARNING") return "#f59e0b";
    if (s === "HEALTHY") return "#10b981";
    return "#e2e8f0";
  };

  // Net score is 1-25; render as a fraction of the rim for an at-a-glance
  // read of control health without opening the sidebar.
  const pct = hasScore ? Math.max(0, Math.min(1, netScore / 25)) : 0;
  const dashOffset = RING_CIRCUMFERENCE * (1 - pct);

  return (
    <div
      className="z-20 w-44 h-44 rounded-full bg-white shadow-[0_8px_30px_-8px_rgba(0,0,0,0.1)] flex flex-col items-center justify-center p-4 text-center group relative"
      data-testid="CARD_control_canvas_node"
    >
      {/* Score rim */}
      <svg
        className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none"
        viewBox="0 0 176 176"
      >
        <circle
          cx="88"
          cy="88"
          r={RING_RADIUS}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth="6"
        />
        {hasScore && (
          <circle
            cx="88"
            cy="88"
            r={RING_RADIUS}
            fill="none"
            stroke={ringColor(signal)}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            className="transition-all duration-500"
          />
        )}
      </svg>

      <div className="absolute top-4 text-[9px] font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">
        {control.control_code}
      </div>

      <div className="bg-emerald-50 p-2 rounded-full mb-1 ring-4 ring-emerald-50/50">
        <Shield size={18} className="text-emerald-500" />
      </div>

      <h2 className="text-xs font-bold text-slate-800 leading-snug line-clamp-3 w-32">
        {control.control_name}
      </h2>

      <div className="mt-2 flex flex-col items-center">
        <div className={badgeClass(signal)}>
          {hasScore ? netScore : "—"}
        </div>
        <span className="text-[8px] text-slate-400 uppercase tracking-widest mt-1 font-bold">
          Net Control Score
        </span>
      </div>
    </div>
  );
};

// Right Sidebar — Test Results summary + score journey (control analogue of the
// risk canvas's Assessment Details + Risk Journey).
const RightSidebar = ({
  control,
  history,
  onAddScore,
  onDeleteScore,
  hasControlId,
  canModify,
}: {
  control: ControlRegister;
  history: ControlScoreHistoryRow[];
  onAddScore: () => void;
  onDeleteScore: (id: number) => void;
  hasControlId: boolean;
  canModify: boolean;
}) => {
  const { allowed: canScore } = usePermission("CTR_CONTROL_LIBRARY", "view+modify");
  const { formatDate } = useEntitySettings();

  const getScoreColor = (score?: number | null) => {
    if (score == null) return "status-badge xs";
    if (score >= 15) return "status-badge xs success";
    if (score >= 8) return "status-badge xs warning";
    return "status-badge xs danger";
  };

  // Latest score event drives the summary — same live fetch as the center
  // node and header strip, not the (possibly stale) control rollup fields.
  const latest = history[0];

  return (
    <div className="w-80 shrink-0 flex flex-col gap-4 border-l border-slate-200 p-6 bg-slate-50/50 h-full overflow-hidden">
      {/* Test Summary Block */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
          <Calendar size={12} /> Test Summary
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-500">Last Check</span>
            <span className="font-medium text-slate-700">
              {latest ? formatDate(latest.scored_on) : "—"}
            </span>
          </div>
          <div className="h-px bg-slate-100" />
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-50 p-2 rounded border border-slate-100 text-center">
              <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">
                Effectiveness
              </div>
              <div className="text-lg font-black text-slate-700">
                {latest?.effectiveness_score ?? "—"}
              </div>
            </div>
            <div className="bg-slate-50 p-2 rounded border border-slate-100 text-center">
              <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">
                Existence
              </div>
              <div className="text-lg font-black text-slate-700">
                {latest?.existence_score ?? "—"}
              </div>
            </div>
          </div>
          <div className={getScoreColor(latest?.net_control_score) + " flex justify-between items-center"}>
            <span className="font-medium">Net Score (latest)</span>
            <span className="font-bold">{latest?.net_control_score ?? "—"}</span>
          </div>
        </div>
      </div>

      {/* History Log */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col min-h-0">
        <div className="flex justify-between items-center mb-4 shrink-0">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
            <History size={12} /> Test Journey
          </h3>
          {hasControlId && (
            <button
              onClick={() => {
                if (!canScore) { swalError("You do not have permission to record a score for this control."); return; }
                onAddScore();
              }}
              className={`text-[10px] font-bold uppercase tracking-wider bg-blue-600 text-white px-2 py-1 rounded flex items-center gap-1 transition-colors${canScore ? " hover:bg-blue-700" : " opacity-50 cursor-not-allowed"}`}
              title={canScore ? "Record new score" : "Not authorised"}
              data-testid="BTN_control_canvas_score_new"
            >
              <Plus size={12} /> New
            </button>
          )}
        </div>

        <div className="relative pl-2 flex-1 overflow-y-auto custom-scrollbar pr-2 min-h-0">
          {history.length === 0 && (
            <div className="text-[11px] italic text-slate-400 text-center py-6">
              {hasControlId
                ? 'No score events yet. Click "+ New" to record one.'
                : "Open from a control to record scores."}
            </div>
          )}
          {history.length > 0 && (
            <div className="absolute left-[19px] top-2 bottom-4 w-0.5 bg-slate-200" />
          )}

          <div className="space-y-6 relative">
            {history.map((entry) => (
              <div key={entry.id} className="flex gap-4 relative group">
                <div className={getScoreColor(entry.net_control_score)}>
                  {entry.net_control_score ?? "—"}
                </div>
                <div className="flex-1 pt-1 min-w-0">
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-xs font-bold text-slate-700">
                      {formatDate(entry.scored_on)}
                    </span>
                    <div className="flex items-center gap-1">
                      {entry.source === "adhoc" && (
                        <button
                          onClick={() => {
                            if (!canModify) { swalError("You do not have permission to modify this record."); return; }
                            onDeleteScore(entry.id);
                          }}
                          className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                          title={canModify ? "Delete score event" : "Not authorised"}
                          data-testid={`BTN_control_canvas_score_${entry.id}_delete`}
                        >
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {entry.test_name ? `Test: ${entry.test_name}` : "Adhoc"}
                    {entry.maker_name ? ` · ${entry.maker_name}` : ""}
                    {entry.location_name ? ` · ${entry.location_name}` : ""}
                  </div>
                  {entry.remarks && (
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                      {entry.remarks}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// SVG Connectors — a Y, not a bow-tie diamond: Process (top-left) and Risk
// (top-right) both funnel down into the center node; Implementation flows out
// as the single stem below.
const Connectors = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none z-0 hidden md:block overflow-visible"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
  >
    {/* Blue: Process (TL) -> Center Top */}
    <path
      d="M 32 20 L 50 20 L 50 37"
      stroke="#60a5fa"
      strokeWidth="1"
      fill="none"
      opacity="0.8"
      strokeLinejoin="round"
    />
    {/* Green: Risk (TR) -> Center Top */}
    <path
      d="M 68 20 L 50 20 L 50 37"
      stroke="#34d399"
      strokeWidth="1"
      fill="none"
      opacity="0.8"
      strokeLinejoin="round"
    />
    {/* Slate: Center Bottom -> Implementation (stem) */}
    <path
      d="M 50 63 L 50 80"
      stroke="#94a3b8"
      strokeWidth="1.25"
      fill="none"
      opacity="0.8"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Main App ---

export interface ControlVisualizationProps {
  control?: ControlRegister;
  onClose?: () => void;
  canModify?: boolean;
}

export default function ControlVisualization({
  control,
  onClose,
  canModify = false,
}: ControlVisualizationProps) {
  const controlDbId: number | undefined = control?.id;

  const [scoreHistory, setScoreHistory] = useState<ControlScoreHistoryRow[]>([]);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const { formatDate } = useEntitySettings();

  const loadScores = useCallback(async () => {
    if (!controlDbId) return;
    try {
      const page = await controlRegisterService.getScoreHistory(controlDbId, { page: 1, limit: 20 });
      setScoreHistory(page.rows);
    } catch {
      setScoreHistory([]);
    }
  }, [controlDbId]);

  useEffect(() => {
    loadScores();
  }, [loadScores]);

  const handleDeleteScore = async (scoreId: number) => {
    if (!controlDbId) return;
    const ok = await confirmDelete({ title: "Delete this score event?" });
    if (!ok) return;
    try {
      await controlRegisterService.deleteScoreHistory(controlDbId, scoreId);
      await loadScores();
    } catch (e) {
      swalError("Failed to delete score");
    }
  };

  // Process (procedures) and Risk are real links (ctr_procedure_maps /
  // rsk_control_maps); Implementations are the control's own downstream
  // rollout steps — read-only here (managed from ControlConfigModal's
  // dedicated Implementations tab, mirroring how the risk canvas treats
  // its derived Processes quadrant as read-only).
  const [processes, setProcesses] = useState<LinkedItem[]>([]);
  const [risks, setRisks] = useState<LinkedItem[]>([]);
  const [implementations, setImplementations] = useState<ControlImplementationStep[]>([]);
  const [configTab, setConfigTab] = useState<ControlLinkType | null>(null);
  const [selectedRisk, setSelectedRisk] = useState<RiskRegister | null>(null);
  const [loadingRisk, setLoadingRisk] = useState(false);

  const handleOpenRisk = async (riskId: number) => {
    if (loadingRisk) return;
    setLoadingRisk(true);
    try {
      const r = await riskRegisterService.getById(riskId);
      setSelectedRisk(r);
    } catch (e) {
      swalError("Failed to load risk details");
    } finally {
      setLoadingRisk(false);
    }
  };

  const loadLinks = useCallback(async () => {
    if (!controlDbId) return;
    try {
      const [p, r, impl] = await Promise.all([
        linkService.listForControl(controlDbId, "procedures"),
        linkService.listForControl(controlDbId, "risks"),
        controlRegisterService.getControlImplementations(controlDbId),
      ]);
      setProcesses(p);
      setRisks(r);
      setImplementations(impl);
    } catch {
      // leave whatever we had
    }
  }, [controlDbId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const toListItems = (rows: LinkedItem[]) =>
    rows.map((r) => ({
      id: r.id,
      text: r.code
        ? `${r.code} — ${r.name}`
        : r.step_number != null
          ? `Step ${r.step_number}: ${r.name}`
          : r.name,
    }));

  const implementationItems = implementations.map((i) => ({
    id: i.id,
    text: i.target_label
      ? `${i.target_label}${i.target_subtitle ? ` (${i.target_subtitle})` : ""}`
      : `${i.implementation_type} #${i.target_id}`,
  }));

  const handleLinkRemove = async (kind: ControlLinkType, removedId: number) => {
    if (!controlDbId) return;
    if (!canModify) { swalError("You do not have permission to modify this record."); return; }
    const currentRows = kind === "procedures" ? processes : risks;
    const nextIds = currentRows.filter((r) => r.id !== removedId).map((r) => r.id);
    try {
      await linkService.replaceForControl(controlDbId, kind, nextIds);
      await loadLinks();
    } catch {
      swalError(`Failed to remove ${kind.slice(0, -1)}`);
    }
  };

  const latest = scoreHistory[0];
  const netScore = latest?.net_control_score ?? 0;
  const hasScore = latest?.net_control_score != null;
  const liveSignal = !hasScore
    ? "UNKNOWN"
    : netScore >= 15
      ? "HEALTHY"
      : netScore >= 8
        ? "WARNING"
        : "BREACH";

  return (
    <div className="bg-white text-slate-800 overflow-hidden flex flex-col h-full">
      {/* Top Header */}
      <div className="bg-white border-b border-slate-100 p-4 flex justify-between items-center z-20 relative">
        <div>
          <h1 className="text-xl font-black text-slate-800 tracking-tight">
            CONTROL FLOW CANVAS
          </h1>
          <p className="text-slate-400 text-[10px] font-medium uppercase tracking-widest mt-0.5">
            Process &amp; Risk → Control → Implementation
          </p>
        </div>
        <div className="flex items-center gap-4">
          {latest ? (
            <div className="hidden md:flex items-stretch gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
              <div className="flex flex-col items-center px-2">
                <span className="text-[8px] text-slate-400 uppercase font-bold tracking-widest">
                  Effectiveness
                </span>
                <span className="text-sm font-black text-slate-700 leading-tight">
                  {latest.effectiveness_score ?? "—"}
                </span>
              </div>
              <div className="w-px bg-slate-200" />
              <div className="flex flex-col items-center px-2">
                <span className="text-[8px] text-slate-400 uppercase font-bold tracking-widest">
                  Net Score
                </span>
                <span
                  className={`text-sm font-black leading-tight ${
                    (latest.net_control_score ?? 0) >= 15
                      ? "text-emerald-600"
                      : (latest.net_control_score ?? 0) >= 8
                        ? "text-orange-600"
                        : "text-red-600"
                  }`}
                >
                  {latest.net_control_score ?? "—"}
                </span>
              </div>
              <div className="w-px bg-slate-200" />
              <div className="flex flex-col items-start px-2">
                <span className="text-[8px] text-slate-400 uppercase font-bold tracking-widest">
                  As of
                </span>
                <span className="text-[10px] text-slate-600 leading-tight">
                  {formatDate(latest.scored_on)}
                </span>
              </div>
            </div>
          ) : (
            <div className="hidden md:flex gap-4 text-[9px] text-slate-400">
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div> PROCESS
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div> RISK
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400"></div> IMPLEMENTATION
              </div>
            </div>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="BTN_control_canvas_close"
            >
              <X size={20} className="text-slate-500" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-row overflow-hidden h-full">
        {/* LEFT: Diagram Canvas */}
        <div className="flex-1 relative bg-white p-4 overflow-y-auto h-full min-w-0">
          <div className="w-full h-full max-w-4xl mx-auto flex flex-col justify-center min-h-[800px]">
            <div className="relative w-full aspect-square max-h-[800px] mx-auto">
              <Connectors />

              <div className="absolute inset-0 md:grid md:grid-cols-3 md:grid-rows-3 p-4 gap-4">
                {/* TOP LEFT: PROCESS (Blue) — direct link via ctr_procedure_maps */}
                <div className="md:col-start-1 md:row-start-1 flex justify-center md:justify-start md:items-start z-10">
                  <ListNode
                    title="Process"
                    icon={Activity}
                    color="blue"
                    items={toListItems(processes)}
                    onRemove={(id: number) => handleLinkRemove("procedures", id)}
                    onManage={
                      controlDbId ? () => setConfigTab("procedures") : undefined
                    }
                  />
                </div>

                {/* TOP RIGHT: RISK (Green) — direct link via rsk_control_maps */}
                <div className="md:col-start-3 md:row-start-1 flex justify-center md:justify-end md:items-start z-10">
                  <ListNode
                    title="Risk"
                    icon={AlertTriangle}
                    color="green"
                    items={toListItems(risks)}
                    onRemove={(id: number) => handleLinkRemove("risks", id)}
                    onManage={
                      controlDbId ? () => setConfigTab("risks") : undefined
                    }
                    onItemClick={handleOpenRisk}
                  />
                </div>

                {/* CENTER: Control Node */}
                <div className="md:col-start-2 md:row-start-2 flex justify-center items-center z-20 pointer-events-none">
                  <div className="pointer-events-auto">
                    {control ? (
                      <ControlNode
                        control={control}
                        netScore={netScore}
                        hasScore={hasScore}
                        signal={liveSignal}
                      />
                    ) : (
                      <div className="w-44 h-44 rounded-full bg-slate-50 border-[5px] border-slate-100" />
                    )}
                  </div>
                </div>

                {/* BOTTOM (stem): IMPLEMENTATION (Slate) — read-only here, managed
                    from the control's own Implementations tab */}
                <div className="md:col-start-2 md:row-start-3 flex justify-center md:items-end z-10">
                  <ListNode
                    title="Implementation"
                    icon={Wrench}
                    color="slate"
                    items={implementationItems}
                    onRemove={() => {}}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Sidebar */}
        {control && (
          <RightSidebar
            control={control}
            history={scoreHistory}
            hasControlId={!!controlDbId}
            onAddScore={() => setScoreModalOpen(true)}
            onDeleteScore={handleDeleteScore}
            canModify={canModify}
          />
        )}
      </div>

      {controlDbId && (
        <ScoreControlModal
          isOpen={scoreModalOpen}
          onClose={() => setScoreModalOpen(false)}
          controlId={controlDbId}
          controlName={control?.control_name}
          onSaved={loadScores}
        />
      )}

      {selectedRisk && (
        <RiskConfigModal
          isOpen={!!selectedRisk}
          onClose={() => setSelectedRisk(null)}
          risk={{
            id: selectedRisk.id,
            code: selectedRisk.code,
            risk_code: selectedRisk.risk_code,
            risk_name: selectedRisk.risk_name,
            description: selectedRisk.description,
            category_id: selectedRisk.category_id,
            status: selectedRisk.status,
            is_active: selectedRisk.is_active,
          }}
          initialTab="canvas"
          onSaved={() => loadLinks()}
        />
      )}

      {controlDbId && configTab && control && (
        <ControlConfigModal
          isOpen
          onClose={() => {
            setConfigTab(null);
            loadLinks();
          }}
          control={control}
          initialTab={configTab}
          onSaved={() => loadLinks()}
        />
      )}
    </div>
  );
}
